import React, { useState, useCallback, useEffect, useRef } from "react";
import parsePhoneNumber, {
  getAsYouType,
} from "@gethere/common/utils/parsePhoneNumber";
import countryToFlag from "../utils/countryToFlag";
import { useIntl } from "react-intl";
import { Input } from "./ui/input";
import countries from "@gethere/common/countries";
import clsx from "clsx";

// Import Lucide icons to replace HeroIcons
import { Check, ChevronDown, X, Search } from "lucide-react";

// Import shadcn UI components
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@gethere/web/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@gethere/web/components/ui/popover";
import { Label } from "./ui/label";
import { count } from "console";

const getDefaultRegion = () =>
  window.navigator.language.indexOf("GB") ? "GB" : "US";

export const MobileInput = ({
  value = "",
  label,
  onChange,
  autoComplete = "tel",
  className = null,
  defaultRegion = null,
  name,
  touched = false,
  disabled = false,
  helperText = null,
  clearValue = null,
  clearable = false,
  error = null,
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> & {
  value: string;
  label?: string;
  defaultRegion?: string;
  clearValue?: any;
  clearable?: boolean;
  helperText?: string;
  error?: string;
  onChange: (v: string) => any;
  touched?: boolean;
}) => {
  const intl = useIntl();
  const [touchedState, setTouch] = useState(Boolean(touched));
  const [focused, setFocus] = useState(false);
  const parentBlur = props?.onBlur;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const triggerRef = useRef<HTMLDivElement>(null);

  const onBlur = useCallback(
    (e) => {
      parentBlur?.(e);
      if (!touchedState) setTouch(true);
      setFocus(false);
    },
    [touchedState, setTouch, parentBlur]
  );

  const onFocus = useCallback(() => {
    setFocus(true);
  }, [setFocus]);

  const [region, setRegion] = useState(() => {
    try {
      return (
        parsePhoneNumber(value).regionCode ||
        defaultRegion ||
        getDefaultRegion()
      );
    } catch (error) {
      return defaultRegion || getDefaultRegion();
    }
  });

  const countryInfo = countries[region];

  useEffect(() => {
    try {
      const nr = parsePhoneNumber(value).regionCode;
      if (nr && nr !== region) setRegion(nr);
    } catch (error) {}
  }, [region, value, setRegion]);

  const ayt = getAsYouType(region);
  ayt.reset(value);
  const p = ayt.getPhoneNumber();

  const valid = Boolean(p?.valid);
  const possible = Boolean(p?.possible);

  const errorState = p
    ? focused
      ? !possible && value?.trim?.().length > 5
      : (touchedState && !valid) || !!error
    : error;

  const message = errorState
    ? intl.formatMessage({
        id: "mobile_challenge_invalid_number",
      })
    : helperText;

  const messageProps = { [errorState ? "error" : "helperText"]: message };

  const inputValue = valid ? p?.number?.national : ayt?.number?.();

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const empty = event.target.value?.length === 0;
    if (empty) return onChange("");
    ayt.reset(event.target.value);
    const number = ayt.getPhoneNumber();
    if (number.valid) {
      const r = number.regionCode;
      if (r !== region) setRegion(r);
      onChange(number.number?.e164);
    } else {
      onChange(event.target.value);
    }
  };

  const onCountrySelect = (code, pre) => {
    setRegion(code);
    setIsOpen(false);
    onChange(p?.number?.e164?.replace?.("+" + p.countryCode, "+" + pre) ?? "");
  };

  const countryCode = p?.countryCode;

  // Filter countries based on search term
  const filteredCountries = Object.keys(countries).filter((code) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const country = countries[code];
    return (
      country.name.toLowerCase().includes(search) ||
      code.toLowerCase().includes(search) ||
      `+${country.phoneCode}`.includes(search)
    );
  });

  const phonePrefix = countryCode || countryInfo?.phoneCode;
  const phonePrefixString = `+${phonePrefix}`;

  return (
    <div className="relative w-full flex flex-col gap-2">
      {label ? <Label htmlFor={name}>{label}</Label> : null}
      <div
        className={clsx(
          "relative w-full",
          errorState ? "text-destructive" : "text-foreground"
        )}
      >
        {/* Country Selector - Left side */}
        <div className="absolute left-0 inset-y-0 flex items-center pl-3 pointer-events-auto z-10 max-w-[5.7rem]">
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <div
                ref={triggerRef}
                className={clsx(
                  "flex items-center gap-1 cursor-pointer px-1 py-0.5 rounded truncate",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
                onClick={() => !disabled && setIsOpen(true)}
              >
                <span className="text-lg flex-shrink-0">
                  {countryToFlag(region)}
                </span>
                <span className="text-sm text-muted-foreground truncate">
                  {phonePrefixString}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 w-[220px]"
              align="start"
              sideOffset={5}
              avoidCollisions={true}
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search country..."
                  className="h-9"
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                />
                <CommandList>
                  <CommandEmpty>No country found.</CommandEmpty>
                  <CommandGroup>
                    {filteredCountries.map((cc) => {
                      const country = countries[cc];
                      if (!country) return null;

                      const isSelected = cc === region;
                      return (
                        <CommandItem
                          key={cc}
                          onSelect={() =>
                            onCountrySelect(cc, country.phoneCode)
                          }
                          className={clsx(
                            "flex justify-between",
                            isSelected && "bg-primary/10 dark:bg-primary-900/30"
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <span>{countryToFlag(cc)}</span>
                            <span className={isSelected ? "font-medium" : ""}>
                              {country.name}
                            </span>
                          </span>
                          <span className="text-muted-foreground">
                            +{country.phoneCode}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Main Input */}
        <Input
          {...props}
          className={clsx(
            "w-full pl-[5.7rem] pr-8",
            errorState && "border-destructive focus-visible:ring-destructive",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          name={name}
          disabled={disabled}
          type="tel"
          autoComplete={autoComplete}
          value={inputValue}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={onInputChange}
        />

        {/* Valid Check Icon - Right side */}
        {p?.valid && !error && (
          <div className="absolute right-3 inset-y-0 flex items-center">
            <Check className="h-4 w-4 text-muted-foreground" />
          </div>
        )}

        {/* Clear button */}
        {clearable && value && (
          <div
            className="absolute right-3 inset-y-0 flex items-center cursor-pointer"
            onClick={() => onChange("")}
          >
            <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </div>
        )}
      </div>

      {/* Error message */}
      {messageProps.error && (
        <p className="text-xs mt-1 text-destructive">{messageProps.error}</p>
      )}

      {/* Helper text */}
      {messageProps.helperText && !messageProps.error && (
        <p className="text-xs mt-1 text-muted-foreground">
          {messageProps.helperText}
        </p>
      )}
    </div>
  );
};
